import { Component, Input, Output, EventEmitter, OnInit, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { SubscriptionService } from '../../../../Utils/_services/admin/subscription/subscription.service';
import { BillingCycle, ISubscription, SubscriptionPlan } from '../../../../Utils/_models/subscription.model';


@Component({
  selector: 'app-add-subscription-modal',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './add-subscription-modal.component.html',
  styleUrl: './add-subscription-modal.component.css',
})
export class AddSubscriptionModalComponent implements OnInit {
  @Input() visible = false;
  @Input() plans: SubscriptionPlan[] = [];
  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<ISubscription>();

  saving = signal(false);

  form = {
    name: '',
    email: '',
    plan: null as SubscriptionPlan | null,
    billingCycle: 'Monthly' as BillingCycle,
  };

  billingCycles: BillingCycle[] = ['Monthly', 'Yearly'];

  errors: Record<string, string> = {};

  constructor(private subscriptionService: SubscriptionService) {}

  ngOnInit(): void {}

  validate(): boolean {
    this.errors = {};
    if (!this.form.name.trim()) this.errors['name'] = 'Name is required';
    if (!this.form.email.trim()) this.errors['email'] = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email))
      this.errors['email'] = 'Enter a valid email';
    if (!this.form.plan) this.errors['plan'] = 'Plan is required';
    return Object.keys(this.errors).length === 0;
  }

  submit(): void {
    if (!this.validate()) return;
    this.saving.set(true);

    const initials = this.form.name
      .split(' ')
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    const avatarBgs = ['#f0abfc', '#a5f3fc', '#bbf7d0', '#fde68a', '#c7d2fe', '#fed7aa', '#f9a8d4'];
    const avatarBg = avatarBgs[Math.floor(Math.random() * avatarBgs.length)];

    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });

    const newSub: Omit<ISubscription, 'id'> = {
      user: { name: this.form.name, email: this.form.email, initials, avatarBg },
      plan: this.form.plan!,
      status: 'Pending',
      date: today,
      amount: this.getPlanAmount(this.form.plan!),
      billingCycle: this.form.billingCycle,
      isActive: true,
      nextBillingDate: this.getNextBillingDate(this.form.billingCycle),
      features: this.getPlanFeatures(this.form.plan!),
    };

    this.subscriptionService
      .create(newSub)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe(saved => this.onSave.emit(saved));
  }

  private getPlanAmount(plan: SubscriptionPlan): number {
    const map: Record<SubscriptionPlan, number> = { Free: 0, 'Basic Plan': 9.99, 'Pro Plan': 29.99, Enterprise: 99.99 };
    return map[plan];
  }

  private getPlanFeatures(plan: SubscriptionPlan): string[] {
    const map: Record<SubscriptionPlan, string[]> = {
      Free: ['Up to 3 events', 'Community support'],
      'Basic Plan': ['Up to 10 events', 'Email support', 'Basic analytics'],
      'Pro Plan': ['Unlimited events', 'Priority support', 'Advanced analytics', 'Custom branding'],
      Enterprise: ['Unlimited everything', 'Dedicated support', 'Custom integrations', 'SLA guarantee', 'White-label'],
    };
    return map[plan];
  }

  private getNextBillingDate(cycle: BillingCycle): string {
    const d = new Date();
    if (cycle === 'Monthly') d.setMonth(d.getMonth() + 1);
    else d.setFullYear(d.getFullYear() + 1);
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  close(): void {
    this.onClose.emit();
  }
}
